import React, { useEffect, useMemo, useState } from 'react'
import { View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { compact } from 'lodash'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { Text } from '@src/components/base/Text'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button } from '@src/components/base/Button'
import { Icon } from '@src/components/base/Icon'
import {
  EhrAppointmentProviderKind,
  EhrAppointmentRecurrenceCadence,
  EhrAppointmentRecurrenceDay,
  PotentiallyRecurringSlots,
} from '@src/types'
import {
  toStartCaseOption,
  toTimeSlotOption,
} from '@src/screens/NutritionistHub/components/UpcomingCalls/helpers'
import {
  INDEFINITELY_RECURRING_CALLS,
  orderedCadenceOptions,
  TIME_SLOT_FORMAT,
} from '@src/screens/NutritionistHub/components/UpcomingCalls/constants'
import { CommonSelect } from '@src/components/CommonSelect'
import { MultiSelect } from '@src/components/MultiSelect'
import { Feature, useFeatureFlag } from '@src/components/FeatureFlag'
import { useDayTimeOptions } from '@src/screens/NutritionistHub/components/UpcomingCalls/useDayTimeOptions'
import {
  ehrAppointmentSchedulingInfoSelector,
  topPriorityCoachAssignmentSelector,
} from '@src/screens/NutritionistHub/models/nutritionistHub.selectors'
import { CustomEventTypes } from '@src/config/analytics'
import { useNutritionistHubTracker } from '@src/screens/NutritionistHub/hooks'
import { useSnack } from '@src/utils'
import { userSelector } from '@src/selectors/app'
// eslint-disable-next-line max-len
import { getSchedulingInfoForCoachingKind } from '@src/screens/NutritionistHub/components/NextCall/useCallSchedulingState'
import { useWeightLossExperience } from './context'

export const ScheduleCalls = () => {
  const { goToNextStep } = useWeightLossExperience()
  const styles = useStyleSheet(themedStyles)
  const dispatch = useDispatch()
  const track = useNutritionistHubTracker()
  const showSnack = useSnack()
  const recurringAppointmentsSchedulingEnabled = useFeatureFlag(
    Feature.RecurringAppointmentsScheduling,
  )

  const [
    potentiallyRecurringSlots,
    setPotentiallyRecurringSlots,
  ] = useState<PotentiallyRecurringSlots | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const user = useSelector(userSelector)
  const coachAssignment = useSelector(topPriorityCoachAssignmentSelector)
  const allSchedulingInfo = useSelector(ehrAppointmentSchedulingInfoSelector)
  const primaryCoachingKind =
    user && 'primaryCoachingKind' in user ? user.primaryCoachingKind : undefined

  const coachingSchedulingInfo = useMemo(() => {
    const coachingKind = primaryCoachingKind?.key

    if (!coachingKind) {
      return undefined
    }

    return getSchedulingInfoForCoachingKind(allSchedulingInfo, coachingKind)
  }, [allSchedulingInfo, primaryCoachingKind])
  const appointmentKind = coachingSchedulingInfo?.options.find((o) => o.recurring)?.appointmentKind

  const defaultValues = {
    cadence: toStartCaseOption(EhrAppointmentRecurrenceCadence.Weekly),
    days: [] as ReturnType<typeof toStartCaseOption>[],
    timeSlots: [] as ReturnType<typeof toTimeSlotOption>[],
  }

  const { control, handleSubmit } = useForm({ defaultValues })

  const days = useWatch({ control, name: 'days' })
  const timeSlots = useWatch({ control, name: 'timeSlots' })
  const cadence = useWatch({ control, name: 'cadence' })

  const isValid = days.length > 0 && timeSlots.length > 0 && appointmentKind

  const { dayOptions, timeSlotOptions } = useDayTimeOptions({
    availabilities: potentiallyRecurringSlots,
    cadence: cadence.value,
    days: days.map((day) => day.value) as EhrAppointmentRecurrenceDay[],
  })

  useEffect(() => {
    if (!coachAssignment?.coach || !coachingSchedulingInfo || !appointmentKind) {
      return
    }

    dispatch({
      type: 'nutritionistHub/fetchPotentiallyRecurringSlots',
      payload: {
        appointmentKind,
        providerKind: EhrAppointmentProviderKind.Coach,
        providerId: coachAssignment.coach.id,
      },
      success: (response: PotentiallyRecurringSlots) => {
        setPotentiallyRecurringSlots(response)
        if (
          response.weekly.length === 0 &&
          response.biWeekly.length === 0 &&
          response.monthly.length === 0
        ) {
          showSnack(
            'No available days found for recurring calls. Please contact support.',
            null,
            'warning',
          )
          track(CustomEventTypes.AppointmentRecurrenceEmptyAvailability, {
            usePotentiallyRecurringSlots: true,
          })
        }
      },
    })
  }, [dispatch, coachAssignment, track, showSnack, coachingSchedulingInfo, appointmentKind])

  const handleScheduleCalls = (formData: typeof defaultValues) => {
    if (!isValid) {
      return
    }

    setSubmitting(true)

    const recurrence = {
      cadence: formData.cadence.value as EhrAppointmentRecurrenceCadence,
      days: formData.days.map((day) => day.value as EhrAppointmentRecurrenceDay),
      numberOfCalls: recurringAppointmentsSchedulingEnabled ? INDEFINITELY_RECURRING_CALLS : 4,
      timeSlots: formData.timeSlots.map((timeSlot) => moment(timeSlot.value, TIME_SLOT_FORMAT)),
      appointmentKind,
    }

    dispatch({
      type: 'nutritionistHub/upsertAppointmentRecurrence',
      payload: recurrence,
      success: () => {
        goToNextStep()
      },
      failure: () => {
        showSnack('An error occurred. Please try again.', null, 'error')
      },
      complete: () => {
        setSubmitting(false)
      },
    })
  }

  return (
    <>
      <View style={{ alignItems: 'center', width: '100%' }}>
        <Controller
          control={control}
          name="cadence"
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <View style={{ marginBottom: 24, width: '100%' }}>
              <CommonSelect
                options={orderedCadenceOptions.map(toStartCaseOption)}
                selectedOption={value}
                label="How frequently would you like to have your calls?"
                placeholder="Cadence"
                onSelect={onChange}
              />
              <Text type="regular" style={styles.caption}>
                Select all that apply.
              </Text>
            </View>
          )}
        />
        <Controller
          control={control}
          name="days"
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <View style={{ marginBottom: 24, width: '100%' }}>
              <MultiSelect
                options={dayOptions}
                selectedOptions={value}
                label="What are your preferred day(s) of the week?"
                placeholder="Days"
                // compacting since previously selected day might not be available anymore
                onSelect={(option) => onChange(compact(option))}
              />
              <Text type="regular" style={styles.caption}>
                Select all that apply.
              </Text>
            </View>
          )}
        />
        <Controller
          control={control}
          name="timeSlots"
          render={({ field: { value, onChange } }) => (
            <View style={{ marginBottom: 24, width: '100%' }}>
              <MultiSelect
                options={timeSlotOptions}
                selectedOptions={value}
                label="What are your preferred time slots?"
                placeholder="Time Slots"
                // compacting since previously selected slot might not be available anymore
                onSelect={(option) => onChange(compact(option))}
              />
              <Text type="regular" style={styles.caption}>
                Select all that apply.
              </Text>
            </View>
          )}
        />
      </View>

      <Button
        type="primary"
        size="block"
        accessoryRight={<Icon name="arrow-right" />}
        accessibilityLabel="Continue"
        onPress={handleSubmit(handleScheduleCalls)}
        disabled={!isValid || submitting}
        style={styles.button}
      >
        Schedule Recurring Calls
      </Button>
    </>
  )
}

const themedStyles = StyleService.create({
  title: {
    fontSize: 24,
    textAlign: 'center',
    maxWidth: 240,
    marginBottom: 16,
  },
  caption: {
    marginTop: 4,
    color: 'theme.text.secondary',
  },
  button: {
    width: '100%',
  },
})
